import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Globe, AppWindow, Smartphone, Code2, PenTool, Cloud, ShieldCheck, Headphones, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { EASE } from "@/components/Reveal";

const PROJECT_TYPES = [
  { id: "website", label: "Website", icon: Globe },
  { id: "web_app", label: "Web Application", icon: AppWindow },
  { id: "mobile_app", label: "Mobile App", icon: Smartphone },
  { id: "custom_software", label: "Custom Software", icon: Code2 },
  { id: "uiux", label: "UI/UX Design", icon: PenTool },
  { id: "cloud", label: "Cloud & DevOps", icon: Cloud },
  { id: "security", label: "Cybersecurity", icon: ShieldCheck },
  { id: "managed_it", label: "Managed IT", icon: Headphones },
];

const FEATURES = [
  { id: "cms", label: "CMS / content editing" },
  { id: "ecommerce", label: "E-commerce" },
  { id: "auth", label: "Accounts & login" },
  { id: "payments", label: "Payments" },
  { id: "integrations", label: "Third-party integrations" },
  { id: "analytics", label: "Analytics & reporting" },
  { id: "multilingual", label: "Multi-language" },
  { id: "ai", label: "AI features" },
];

const TIMELINES = [
  { id: "flexible", label: "Flexible", hint: "Up to 45 days" },
  { id: "standard", label: "Standard", hint: "Around 30 days" },
  { id: "accelerated", label: "Fast-track", hint: "Priority team" },
];

const SUPPORT = [
  { id: "none", label: "No ongoing support" },
  { id: "essential", label: "Essential care plan" },
  { id: "premium", label: "Premium 24/7 plan" },
];

const SERVICE_MAP = {
  website: "Website Development",
  web_app: "Website Development",
  mobile_app: "Mobile App Development",
  custom_software: "Custom Software",
  uiux: "UI/UX Design",
  cloud: "Cloud Solutions",
  security: "Cybersecurity",
  managed_it: "IT Support & Maintenance",
};

export default function QuoteEstimator() {
  const [projectType, setProjectType] = useState("web_app");
  const [features, setFeatures] = useState(["auth"]);
  const [timeline, setTimeline] = useState("standard");
  const [support, setSupport] = useState("none");

  const toggleFeature = (id) =>
    setFeatures((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));

  const navigate = useNavigate();

  const chipCls = (active) =>
    `cursor-pointer rounded-lg border px-3.5 py-2.5 text-sm transition-all ${
      active
        ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200 shadow-[0_0_18px_rgba(0,240,255,0.15)]"
        : "border-white/10 bg-white/5 text-slate-300 hover:border-white/25"
    }`;

  const goToQuote = () => {
    const pt = PROJECT_TYPES.find((t) => t.id === projectType);
    const feats = features.map((f) => FEATURES.find((x) => x.id === f)?.label).filter(Boolean);
    const tl = TIMELINES.find((t) => t.id === timeline);
    const sp = SUPPORT.find((s) => s.id === support);
    const message = `Quote request\nProject type: ${pt?.label}\nFeatures: ${feats.length ? feats.join(", ") : "None selected"}\nTimeline: ${tl?.label} (${tl?.hint})\nSupport: ${sp?.label}\n\nMore about the project: `;
    navigate(`/contact?service=${encodeURIComponent(SERVICE_MAP[projectType] || "")}&message=${encodeURIComponent(message)}`);
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_380px]" data-testid="quote-estimator">
      <div className="space-y-10">
        <section>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Step 01 — Project type</p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {PROJECT_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setProjectType(t.id)}
                data-testid={`quote-type-${t.id}`}
                className={`flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all ${
                  projectType === t.id
                    ? "border-cyan-400/60 bg-cyan-400/10 shadow-[0_0_22px_rgba(0,240,255,0.15)]"
                    : "border-white/10 bg-white/5 hover:border-white/25"
                }`}
              >
                <t.icon className={`h-5 w-5 ${projectType === t.id ? "text-cyan-300" : "text-slate-400"}`} />
                <span className="text-sm font-medium text-white">{t.label}</span>
              </button>
            ))}
          </div>
        </section>
        <section>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Step 02 — Features</p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {FEATURES.map((f) => (
              <button key={f.id} type="button" onClick={() => toggleFeature(f.id)} className={chipCls(features.includes(f.id))} data-testid={`quote-feature-${f.id}`}>
                <span className="flex items-center gap-2">
                  {features.includes(f.id) && <Check className="h-3.5 w-3.5 text-cyan-300" />}
                  {f.label}
                </span>
              </button>
            ))}
          </div>
        </section>
        <div className="grid gap-10 sm:grid-cols-2">
          <section>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Step 03 — Timeline</p>
            <div className="mt-4 space-y-2.5">
              {TIMELINES.map((t) => (
                <button key={t.id} type="button" onClick={() => setTimeline(t.id)} data-testid={`quote-timeline-${t.id}`} className={`${chipCls(timeline === t.id)} flex w-full items-center justify-between`}>
                  <span>{t.label}</span>
                  <span className="font-mono text-[11px] text-slate-500">{t.hint}</span>
                </button>
              ))}
            </div>
          </section>
          <section>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Step 04 — Ongoing support</p>
            <div className="mt-4 space-y-2.5">
              {SUPPORT.map((s) => (
                <button key={s.id} type="button" onClick={() => setSupport(s.id)} data-testid={`quote-support-${s.id}`} className={`${chipCls(support === s.id)} flex w-full items-center justify-between`}>
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>
      <aside className="lg:sticky lg:top-24 h-fit">
        <div className="glass rounded-2xl p-7" data-testid="quote-summary-panel">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Your project summary</p>
          <motion.dl
            key={`${projectType}-${features.join()}-${timeline}-${support}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="mt-5 space-y-3 border-t border-white/10 pt-5 text-sm"
            data-testid="quote-selection-summary"
          >
            <div className="flex justify-between gap-4"><dt className="text-slate-400">Project type</dt><dd className="text-right font-medium text-white">{PROJECT_TYPES.find((t) => t.id === projectType)?.label}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-slate-400">Features</dt><dd className="text-right font-medium text-white">{features.length ? `${features.length} selected` : "None"}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-slate-400">Timeline</dt><dd className="text-right font-medium text-white">{TIMELINES.find((t) => t.id === timeline)?.label}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-slate-400">Support</dt><dd className="text-right font-medium text-white">{SUPPORT.find((s) => s.id === support)?.label}</dd></div>
          </motion.dl>
          <button
            type="button"
            onClick={goToQuote}
            data-testid="quote-get-quote-button"
            className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-electric px-5 py-3.5 text-sm font-semibold text-white shadow-[0_0_25px_rgba(0,102,255,0.35)] transition-all hover:bg-cyan-400 hover:text-ink"
          >
            Get a Quote
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-slate-500">
            You'll be taken to our contact form with these details pre-filled. We reply with a
            tailored, fixed-price quote within one business day.
          </p>
        </div>
      </aside>
    </div>
  );
}
